import { EdgeLabelRenderer, useViewport } from '@xyflow/react';
import React from 'react';

export type Cursor = {
  id: string;
  color: string;
  x: number;
  y: number;
  name?: string;
  isLocalUser?: boolean;
};

/**
 * Cursors component that displays all active users' cursors in the mindmap
 * Uses EdgeLabelRenderer to ensure proper positioning in the flow
 */
function Cursors({ cursors }: { cursors: Cursor[] }) {
  const viewport = useViewport();

  return (
    <EdgeLabelRenderer>
      {cursors.map(({ id, color, x, y, name, isLocalUser }) => {
        const translate = `translate(${x}px, ${y}px)`;
        const scale = `scale(${1 / viewport.zoom})`;

        return (
          <div key={id} className="collaborative-cursor" style={{ transform: translate }}>
            <div style={{ transform: scale }}>
              {/* Only show cursor for remote users, not for local user */}
              {!isLocalUser && (
                <svg 
                  width="36" 
                  height="36" 
                  viewBox="0 0 32 32" 
                  className="cursor-svg"
                  style={{ 
                    position: 'absolute',
                    top: '-18px',  // Center the cursor on the position
                    left: '-18px'  // Center the cursor on the position
                  }}
                >
                  <path
                    d={cursorPath}
                    fill={color}
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              <div 
                className={`cursor-label ${isLocalUser ? 'cursor-label-local' : ''}`}
                style={{ 
                  backgroundColor: color,
                  color: '#fff',
                  padding: '3px 8px', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  position: 'absolute',
                  // Position the label under the cursor
                  top: isLocalUser ? '0' : '20px',
                  // Center horizontally
                  left: '0',
                  whiteSpace: 'nowrap',
                  fontWeight: isLocalUser ? 'bold' : 'normal',
                  opacity: isLocalUser ? 0.9 : 1,
                  transform: 'translateX(-50%)'
                }}
              >
                {isLocalUser ? 'You' : name || 'User'}
              </div>
            </div>
          </div>
        );
      })}
    </EdgeLabelRenderer>
  );
}

// Clean, simple cursor path for a filled cursor with white outline
const cursorPath = `
  M8 4
  L8 24
  L13 19
  L17 28
  L20 26
  L16 17
  L24 16
  L8 4
  Z
`;

export default Cursors; 